import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function NorthumberlandCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Northumberland Pothole Claims | 212,000 Repairs on 5,209km | Fixtyer"
        description="Northumberland filled over 212,000 potholes in five years on one of England's largest rural networks, yet 18% of unclassified roads are in RED condition and only 25% are surveyed each year. See what the DfT AMBER rating means for your claim."
        canonical="/councils/northumberland"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Northumberland', url: '/councils/northumberland' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Northumberland has a GREEN spend scorecard — can I still claim?',
            answer: 'Yes. The DfT Spend rating is GREEN because Northumberland invests heavily in preventative maintenance — 88% of capital spend was preventative in 2024/25. But the overall rating is AMBER because road condition is AMBER, and Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend figures.',
          },
          {
            question: 'What if my pothole was on an unclassified or rural road?',
            answer: 'U-roads make up 2,539km — 49% of Northumberland\'s 5,209km network. The council collects condition data on only 25% of U-roads each year using Coarse Visual Inspection, not the SCANNER laser surveys used on A, B and C roads. At the latest survey, 18% of U-roads were in RED condition — roughly 457km of rural lanes, village streets and estate roads the council\'s own data says should be considered for maintenance.',
          },
          {
            question: 'Does the 25% annual U-road survey rate weaken a Section 58 defence?',
            answer: 'It can. Northumberland\'s own report states that "due to the size of the County, the length of unclassified roads within it and the survey methodology used, we currently collect condition data on 25% of our Unclassified Road network on an annual basis." At that rate, a given U-road may not appear in network condition data for up to four years — yet potholes can form in days. If the council cannot show your specific road was inspected within a reasonable period before your incident, the gap between survey coverage and defect formation is a structural argument under Section 58.',
          },
          {
            question: 'B and C roads look stable — does that hurt my claim on a classified road?',
            answer: 'Not necessarily, but adjust your strategy. B and C roads in RED condition have edged down from 6% to 5% since 2020, while amber roads still account for 28% — a third of the 2,184km B/C network flagged as needing maintenance soon or now. On classified roads, expect a stronger Section 58 defence backed by SCANNER data. Your claim lives on prior reports, photos showing defect age, and whether the council acted on amber-category warnings for your specific stretch.',
          },
          {
            question: 'Northumberland filled 212,000 potholes in five years — doesn\'t that prove the system works?',
            answer: 'It proves the opposite at network scale. The council repaired 41,950 potholes in 2024/25 alone — roughly 115 every day across the county. A network producing defects at that rate is one where potholes routinely form between inspections. Reactive repair volume is not evidence that preventative maintenance is keeping pace — especially when U-road RED condition has risen from 16% to 18% over the same five-year period.',
          },
          {
            question: 'What does the PAS 2161 methodology change in 2026/27 mean for my claim?',
            answer: 'From 2026/27 Northumberland will switch to BSI PAS 2161, which categorises roads into five condition bands instead of three. The council\'s own report warns this will change how condition is reported nationally. If your incident falls either side of that transition, pre- and post-2026/27 condition records may not be directly comparable — worth noting if the council tries to cite improving headline figures after the methodology change.',
          },
        ]}
      />
      <ServiceSchema 
        councilName="Northumberland County Council" 
        councilSlug="northumberland" 
        rating="amber" 
      />
      <SpeakableSchema
        url="/councils/northumberland"
        headline="Northumberland Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Northumberland</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Northumberland: GREEN Spend, Rising RED on Rural Roads
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Northumberland County Council maintains <strong className="text-white">5,209km of roads</strong> — one of 
            England's largest rural networks — and earns a <strong className="text-emerald-400">GREEN</strong> spend 
            scorecard with 88% preventative investment. Yet the overall rating is <strong className="text-amber-400">AMBER</strong>: 
            RED-condition U-roads have risen from <strong className="text-rose-400">16% to 18%</strong>, only 
            25% of unclassified roads are surveyed each year, and the council filled 
            <strong className="text-rose-400"> over 212,000 potholes in five years</strong>.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">212,069</div>
            <div className="text-white font-medium mt-2">Potholes filled in five years</div>
            <div className="text-brand-300 text-sm mt-1">
              41,950 in 2024/25 alone — roughly 115 pothole repairs every day across a county 
              where 2,539km of unclassified roads are mostly inspected by eye, not laser.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* Network Scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            England's Rural Road Challenge
          </h2>
          <p className="text-surface-600 mb-8">
            Five thousand kilometres of highway — and nearly half of it is unclassified
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Length (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Share of network</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Survey coverage</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center font-mono">484</td>
                  <td className="px-6 py-4 text-center">9.3%</td>
                  <td className="px-6 py-4 text-center">50% annually (SCANNER)</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center font-mono">2,184</td>
                  <td className="px-6 py-4 text-center">41.9%</td>
                  <td className="px-6 py-4 text-center">50% annually (SCANNER)</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-medium text-amber-900">U roads (unclassified)</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">2,539</td>
                  <td className="px-6 py-4 text-center font-bold">48.7%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">25% annually (CVI)</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total roads</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">5,209</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard 
            quote="The highway asset is the most valuable asset owned and managed by Northumberland County Council. It is vitally important for the economy and the quality of life of Northumberland's residents."
            source="Northumberland County Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Northumberland's own transparency report — 
            A-roads improving, U-road RED creeping up
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (484km — 9.3% of network): improving</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">2%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">down from 3% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">18%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">down from 23%</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">80%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 74%</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Credit where due: main roads have genuinely improved on SCANNER data. But A-roads 
              are less than a tenth of the network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (2,184km — 42% of network): broadly stable</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">31%</td>
                    <td className="px-4 py-2 text-center">63%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">29%</td>
                    <td className="px-4 py-2 text-center">65%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">29%</td>
                    <td className="px-4 py-2 text-center">66%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">29%</td>
                    <td className="px-4 py-2 text-center">66%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5%</td>
                    <td className="px-4 py-2 text-center font-bold">28%</td>
                    <td className="px-4 py-2 text-center font-bold">67%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              RED B/C roads have edged down from 6% to 5%, but amber roads still account for 
              28% — roughly 611km flagged as needing maintenance soon. A third of the B/C 
              network sits in amber or red.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">U-roads (2,539km — 49% of network): RED rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">16%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">16%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">17%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">18%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">18%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition U-roads have risen from 16% to 18% over five years — approximately 
              457km of rural lanes, village streets and estate roads the council's own data 
              says should be considered for maintenance. The report publishes RED percentages 
              only for U-roads; amber and green breakdowns are not provided.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend — But Condition Still AMBER</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£34.98m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£32.81m</div>
                <div className="text-brand-300 text-sm">Actual capital spend 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">88%</div>
                <div className="text-brand-300 text-sm">Preventative share 2024/25</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Northumberland spent <span className="text-emerald-400">£32.8m on capital maintenance</span> in 
              2024/25 with 88% classed as preventative — supplemented by £19.1m from the Combined 
              Authority and £7.0m of council funding. The money is there. U-road RED condition 
              is still climbing, and over 212,000 potholes were filled in five years.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Blind Spot */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 2,539km Blind Spot
          </h2>
          <p className="text-surface-600 mb-8">
            Half the network surveyed by eye, a quarter each year — on England's longest rural lanes
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="Due to the size of the County, the length of unclassified roads within it and the survey methodology used, we currently collect condition data on 25% of our Unclassified Road network on an annual basis using the CVI survey method described below."
              source="Northumberland County Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">SCANNER vs CVI</h3>
              <p className="text-surface-600 text-sm mb-3">
                A, B and C roads are assessed with SCANNER laser-based technology — specialist 
                vehicle-mounted lasers measuring surface condition parameters. Unclassified roads 
                get Coarse Visual Inspection: accredited staff visually assessing deterioration 
                along each surveyed section.
              </p>
              <p className="text-surface-600 text-sm">
                The council surveys 50% of A/B/C roads annually (full picture over two years). 
                For U-roads, 25% annually means a given stretch may not appear in network 
                condition data for up to <strong>four years</strong>.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Four-Year Gap</h3>
              <p className="text-amber-800 text-sm mb-3">
                At 18% RED on 2,539km of U-roads, roughly <strong>457km</strong> of unclassified 
                highway is in condition the council categorises as "should be considered for 
                maintenance" — yet the network-level survey only touches a quarter of U-roads 
                each year.
              </p>
              <p className="text-amber-800 text-sm">
                Potholes form in days. Network condition data refreshes in years. That gap 
                is structural, and the council documents it in its own report.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 58 of the Highways Act 1980, a council must show it had a reasonable 
              system for knowing the condition of its roads. For Northumberland's unclassified 
              network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last included in the 25% of U-roads condition-surveyed that year?</li>
              <li>• If 18% of U-roads are RED at the latest survey, what was done about yours specifically?</li>
              <li>• Does a visual CVI inspection on a fraction of the network meet the reasonableness test for rural lanes with heavy agricultural and tourist traffic?</li>
              <li>• Can the council prove highway safety inspections — separate from condition surveys — covered your stretch before your incident?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed network knowledge of roads it condition-surveys 
              at a 25% annual rate using visual inspection — while filling 115 potholes a day.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            212,069 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volume on a network where U-road condition is still deteriorating
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">41,251</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">48,690</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">41,043</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">39,135</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">41,950</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">212,069</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~115 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Northumberland repairs around 115 potholes per day. 
                The council's revenue funding covers "pothole and small patch repairs" alongside 
                safety inspections and emergency response — a network producing defects at this 
                rate is one where potholes routinely form between inspections.
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">The Preventative Argument</h3>
              <p className="text-emerald-800 text-sm">
                The council notes that "every £1 invested in preventative maintenance measures 
                can save at least £4 in future reactive repair costs" — and targets 82–88% 
                preventative spend. Yet U-road RED rose from 16% to 18% over the same period. 
                High preventative spend has not stopped rural road deterioration.
              </p>
            </div>
          </div>

          {/* Spending table */}
          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
            <h3 className="font-semibold text-brand-900 px-6 py-4 border-b border-surface-200">
              Five-Year Spending (capital and revenue, £000s)
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-brand-900">DfT LTP</th>
                    <th className="px-4 py-2 text-center font-semibold text-brand-900">CRSTS</th>
                    <th className="px-4 py-2 text-center font-semibold text-brand-900">NCC</th>
                    <th className="px-4 py-2 text-center font-semibold text-brand-900">Capital spend</th>
                    <th className="px-4 py-2 text-center font-semibold text-brand-900">Revenue spend</th>
                    <th className="px-4 py-2 text-center font-semibold text-brand-900">Preventative</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">£28,380</td>
                    <td className="px-4 py-2 text-center">—</td>
                    <td className="px-4 py-2 text-center">£7,500</td>
                    <td className="px-4 py-2 text-center">£28,281</td>
                    <td className="px-4 py-2 text-center">£10,149</td>
                    <td className="px-4 py-2 text-center text-emerald-600">88%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">£21,780</td>
                    <td className="px-4 py-2 text-center">—</td>
                    <td className="px-4 py-2 text-center">£7,500</td>
                    <td className="px-4 py-2 text-center">£27,402</td>
                    <td className="px-4 py-2 text-center">£11,350</td>
                    <td className="px-4 py-2 text-center text-emerald-600">82%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">£21,780</td>
                    <td className="px-4 py-2 text-center">—</td>
                    <td className="px-4 py-2 text-center">£6,883</td>
                    <td className="px-4 py-2 text-center">£21,538</td>
                    <td className="px-4 py-2 text-center">£11,941</td>
                    <td className="px-4 py-2 text-center text-emerald-600">83%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">£28,420</td>
                    <td className="px-4 py-2 text-center">—</td>
                    <td className="px-4 py-2 text-center">£4,658</td>
                    <td className="px-4 py-2 text-center">£28,133</td>
                    <td className="px-4 py-2 text-center">£12,278</td>
                    <td className="px-4 py-2 text-center text-emerald-600">85%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2024/25</td>
                    <td className="px-4 py-2 text-center">£5,445</td>
                    <td className="px-4 py-2 text-center">£19,103</td>
                    <td className="px-4 py-2 text-center">£6,950</td>
                    <td className="px-4 py-2 text-center">£32,813</td>
                    <td className="px-4 py-2 text-center">£11,881</td>
                    <td className="px-4 py-2 text-center text-emerald-600">88%</td>
                  </tr>
                  <tr className="bg-brand-50">
                    <td className="px-4 py-2 font-mono font-bold">2025/26</td>
                    <td className="px-4 py-2 text-center font-bold">£34,981</td>
                    <td className="px-4 py-2 text-center font-bold">£7,000</td>
                    <td className="px-4 py-2 text-center text-surface-400">TBC</td>
                    <td className="px-4 py-2 text-center text-surface-400">TBC</td>
                    <td className="px-4 py-2 text-center text-surface-400">TBC</td>
                    <td className="px-4 py-2 text-center text-surface-400">TBC</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Forward Plans & Admissions */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            2025/26 Plans and Honest Admissions
          </h2>
          <p className="text-surface-600 mb-8">
            What the council plans next — and what it admits it has not yet done
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">56km</div>
              <div className="text-sm text-surface-600">Resurfaced or reconstructed</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">70km</div>
              <div className="text-sm text-surface-600">Surface dressed</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">7</div>
              <div className="text-sm text-surface-600">Landslip locations remediated</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">6</div>
              <div className="text-sm text-surface-600">Bridge refurbishment schemes</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">£800k</div>
              <div className="text-sm text-surface-600">Footway reconstruction</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">£765k</div>
              <div className="text-sm text-surface-600">Public Rights of Way</div>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="From 2026/27 a new methodology will be used based on the BSI PAS2161 standard. Local Highway Authorities will be required to use a supplier that has been accredited against PAS2161. This new standard will categorise roads into five categories instead of three to help government gain a more detailed understanding of road condition in England."
              source="Northumberland County Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard 
              quote="In order to fully understand the risks our highway network faces from the changing climate and to make it more resilient to these risks we would need to fully adopt a strategic, evidence based, approach that looks to identify vulnerabilities, assess risk and apply adaptation measures into longer term asset management and investment plans."
              source="Northumberland County Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard 
              quote="Further steps will be required to further improve our networks resilience to climate impacts and this work will continue in forthcoming years."
              source="Northumberland County Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Treatment Output Falling</h3>
              <p className="text-surface-600 text-sm mb-3">
                Total carriageway treatment length has dropped from 144.8km in 2020/21 to 
                127.82km in 2024/25 — surface treatment down from 85.3km to 79.9km, and 
                resurfacing from 59.5km to 47.9km. On a 5,209km network, that is roughly 
                2.5% of total road length treated annually.
              </p>
              <p className="text-surface-600 text-sm">
                The 2025/26 programme targets 56km resurfaced plus 70km surface dressed — 
                a modest uplift, but still a fraction of a network where 18% of U-roads 
                alone are in RED condition.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Climate Resilience: Work In Progress</h3>
              <p className="text-amber-800 text-sm">
                The council has identified a core resilient network prioritised for surveys 
                and maintenance, but explicitly states it has not yet fully adopted the 
                strategic, evidence-based approach needed to understand climate risks. 
                Extreme rainfall, flooding and landslips are documented threats — seven 
                landslip remediation schemes are planned for 2025/26 alone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Northumberland invests seriously — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — £32.8m capital spend in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 82–88% preventative maintenance share across five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition genuinely improving (3% RED to 2%)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Published Transport Asset Management Plan aligned with WMHI Code of Practice
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> B/C RED condition edged down from 6% to 5%
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on A-roads and well-trafficked B/C routes. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — U-road RED rose from 16% to 18%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 49% of network surveyed at 25% annually with visual CVI, not laser SCANNER
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 212,069 potholes filled in five years — defects form faster than surveys catch them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Carriageway treatment output falling — 144.8km to 127.8km over five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits climate resilience strategy is not yet fully adopted
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — room to challenge inspection and asset management systems
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a published asset management plan, your claim 
              lives or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the 25% annual CVI survey gap is your strongest structural argument</li>
              <li>• Location on or near a landslip-prone or flood-vulnerable stretch — the council documents these risks</li>
              <li>• Whether your incident falls before or after the 2026/27 PAS 2161 methodology change</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Northumberland's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Northumberland?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road CVI survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No rural network scale context</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road RED rise from 16% to 18% documented</li>
                <li>✅ 25% annual CVI survey gap argued</li>
                <li>✅ 212,069 potholes in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Northumberland</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=northumberland"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Northumberland Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Northumberland has a GREEN spend scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend rating is GREEN because Northumberland invests heavily in preventative 
                maintenance — 88% of capital spend was preventative in 2024/25. But the overall rating is 
                AMBER because road condition is AMBER, and Section 58 turns on whether the specific defect 
                that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend figures.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on an unclassified or rural road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 2,539km — 49% of Northumberland's 5,209km network. The council collects 
                condition data on only 25% of U-roads each year using Coarse Visual Inspection, not the 
                SCANNER laser surveys used on A, B and C roads. At the latest survey, 18% of U-roads 
                were in RED condition — roughly 457km of rural lanes, village streets and estate roads 
                the council's own data says should be considered for maintenance.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the 25% annual U-road survey rate weaken a Section 58 defence?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. Northumberland's own report states that "due to the size of the County, the length 
                of unclassified roads within it and the survey methodology used, we currently collect 
                condition data on 25% of our Unclassified Road network on an annual basis." At that rate, 
                a given U-road may not appear in network condition data for up to four years — yet potholes 
                can form in days. If the council cannot show your specific road was inspected within a 
                reasonable period before your incident, the gap between survey coverage and defect formation 
                is a structural argument under Section 58.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                B and C roads look stable — does that hurt my claim on a classified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily, but adjust your strategy. B and C roads in RED condition have edged down 
                from 6% to 5% since 2020, while amber roads still account for 28% — a third of the 2,184km 
                B/C network flagged as needing maintenance soon or now. On classified roads, expect a stronger 
                Section 58 defence backed by SCANNER data. Your claim lives on prior reports, photos showing 
                defect age, and whether the council acted on amber-category warnings for your specific stretch.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Northumberland filled 212,000 potholes in five years — doesn't that prove the system works?
              </h3>
              <p className="text-surface-600 text-sm">
                It proves the opposite at network scale. The council repaired 41,950 potholes in 2024/25 alone 
                — roughly 115 every day across the county. A network producing defects at that rate is one 
                where potholes routinely form between inspections. Reactive repair volume is not evidence that 
                preventative maintenance is keeping pace — especially when U-road RED condition has risen from 
                16% to 18% over the same five-year period.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does the PAS 2161 methodology change in 2026/27 mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                From 2026/27 Northumberland will switch to BSI PAS 2161, which categorises roads into five 
                condition bands instead of three. The council's own report warns this will change how condition 
                is reported nationally. If your incident falls either side of that transition, pre- and 
                post-2026/27 condition records may not be directly comparable — worth noting if the council 
                tries to cite improving headline figures after the methodology change.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a 
              href="https://www.northumberland.gov.uk/public-transport/public-transport-improvements/transport-plans/local-highways-maintenance"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Northumberland County Council Local Highways Maintenance Transparency Report (June 2025)
            </a>. 
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
